import $ from 'jquery';
import Feature from 'ol/Feature';
import OlMap from 'ol/Map';
import Overlay from 'ol/Overlay';
import View from 'ol/View';
import {
	Attribution,
	FullScreen,
	defaults as defaultControls,
} from 'ol/control.js';
import { createEmpty, extend } from 'ol/extent';
import Point from 'ol/geom/Point';
import TileLayer from 'ol/layer/Tile';
import LayerVector from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import Cluster from 'ol/source/Cluster';
import SourceVector from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

export default class LocationsMap {
	constructor(mapDomElement) {
		this.mapDomElement = mapDomElement;
		this.locationCollection = $(this.mapDomElement).data('location');
		this.filteredLocationCollection = [];
		this.popupOverlayContainer = document.getElementById('popup');
		this.popupOverlayContent = document.getElementById('popup-content');
		this.popupOverlayCloser = document.getElementById('popup-closer');
		this.popupOverlay = new Overlay({
			element: this.popupOverlayContainer,
			autoPan: {
				animation: {
					duration: 250,
				},
			},
		});
		this.filterActive = false;

		this.svgLocation =
			'<svg viewBox="0 0 53 54" width="52.702" height="54.071" xmlns="http://www.w3.org/2000/svg">' +
			'<defs>' +
			'<radialGradient gradientUnits="userSpaceOnUse" cx="29.647" cy="26.106" r="12.439" id="gradient-1" gradientTransform="matrix(1, 0, 0, 1, 0, 0)">' +
			'<stop offset="0" style="stop-color: rgba(0, 0, 0, 1)"/>' +
			'<stop offset="1" style="stop-color: rgba(0, 0, 0, 1)"/>' +
			'</radialGradient>' +
			'</defs>' +
			'<ellipse fill="%23{primary-color}" style="stroke: %23{primary-color}; stroke-width: 2px;" cx="29.761" cy="32.79" rx="19.792" ry="19.792"/>' +
			'<ellipse fill="%23ffffff" style="stroke: %23{primary-color}; stroke-width: 2px;" cx="13.459" cy="14.063" rx="12.632" ry="12.632"/>' +
			'<path fill="%23{primary-color}" style="stroke: %23ffffff; stroke-width: 2px;" d="M 17.418 30.642 L 29.597 21.538 L 42.086 30.675"/>' +
			'<path fill="%23{primary-color}" style="stroke: %23ffffff; stroke-width: 2px;" d="M 20.045 31.971 L 20.045 41.951 L 25.592 41.979 L 25.589 37.296"/>' +
			'<path fill="%23{primary-color}" style="stroke: %23ffffff; stroke-width: 2px;" d="M 33.684 41.966 L 33.684 31.986 L 39.231 31.958 L 39.228 36.641"' +
			' transform="matrix(-1, 0, 0, -1, 72.914993, 73.924004)"/>' +
			'</svg>';

		this.svgCluster =
			'<svg viewBox="0 0 53 54" width="52.702" height="54.071" xmlns="http://www.w3.org/2000/svg">\n' +
			' <ellipse style="stroke: %23{primary-color}; fill: %23{primary-color}; stroke-width: 2px;" cx="29.761" cy="32.79"' +
			' rx="19.792" ry="19.792"/>\n' +
			'</svg>';
	}

	init() {
		this.initBaseLayer();
		this.initMarkerClustering();
		this.initMap();
		this.initEvents();
		this.primaryColor = $(this.mapDomElement)
			.data('primaryColor')
			.replace('#', '');
		this.svgLocation = this.svgLocation.replace(
			/{primary-color}/g,
			this.primaryColor,
		);
		this.svgCluster = this.svgCluster.replace(
			/{primary-color}/g,
			this.primaryColor,
		);
		window.addEventListener('reset-map-features', (event) =>
			this.updateMapFeatures(event, false, this),
		);
		window.addEventListener('update-map-features', (event) =>
			this.updateMapFeatures(event, true, this),
		);
	}

	initBaseLayer() {
		// NOTE: Commented out unused variable
		// const attribution = new Attribution({
		// 	html: `© ${new Date().getFullYear()} <a target="_blank" href="http://www.mapz.com">mapz.com </a>\
		//     - Map Data: <a target="_blank" href="http://openstreetmap.org" >OpenStreetMap</a>\
		//     (<a href="http://opendatacommons.org/licenses/odbl/1.0/" target="_blank">ODbL</a>)`,
		// });
		// Create background layer
		this.baseLayer = new TileLayer({
			source: new XYZ({
				tilePixelRatio: 2,
				url: 'https://tiles.mapz.com/mapproxy/v1/buhck-4fb593af/tiles/1.0.0/mapz_multicolor_base_hq/EPSG3857/{z}/{x}/{-y}.jpeg',
				attributions:
					'© 2022 <a target="_blank" href="http://www.mapz.com">mapz.com </a>\
        - Map Data: <a target="_blank" href="http://openstreetmap.org" >OpenStreetMap</a>\
        (<a href="http://opendatacommons.org/licenses/odbl/1.0/" target="_blank">ODbL</a>)',
			}),
		});
	}

	initMarkerClustering() {
		const locationCollection = this.locationCollection;
		this.features = [];
		let i = 0;
		$.each(locationCollection, (index, locationData) => {
			if (
				this.filterActive &&
				!this.filteredLocationCollection.some(
					(location) => location.uid === locationData.uid,
				)
			) {
				return true;
			}

			const locData = Array(locationData);
			const coordinates = new Point(
				transform(
					[
						Number.parseFloat(locData[0].lng),
						Number.parseFloat(locData[0].lat),
					],
					'EPSG:4326',
					'EPSG:3857',
				),
			);
			this.features[i] = new Feature({
				geometry: coordinates,
			});
			this.features[i].set('data', locData);
			i++;
		});

		this.sourceVector = new SourceVector({
			features: this.features,
		});

		// Create a vector layer with clustering source and add a styling
		this.clusters = new LayerVector({
			// Wrap vector source in cluster source
			source: new Cluster({
				distance: 40,
				source: this.sourceVector,
			}),
			// Define cluster style
			style: (feature) => {
				const size = feature.get('features').length;

				if (size === 1) {
					// Clustered features containing only one feature
					// are treated as normal features
					return [
						new Style({
							image: new Icon({
								//src: '/typo3conf/ext/atl_locations/Resources/Public/Icons/mapz_location.svg',
								src: `data:image/svg+xml;utf8,${this.svgLocation}`,
								scale: 1,
							}),
							text: new Text({
								text: feature.get('features')[0].get('data').length.toString(),
								offsetX: -13,
								offsetY: -12,
								font: 'bold 14px Arial',
								fill: new Fill({
									color: `#${this.primaryColor}`,
								}),
							}),
						}),
					];
				}

				// Otherweise display number of clustered features
				// in the icon
				return [
					new Style({
						geometry: feature.getGeometry(),
						image: new Icon({
							src: `data:image/svg+xml;utf8,${this.svgLocation}`,
							scale: 1,
						}),
						text: new Text({
							text: size.toString(),
							offsetX: -13,
							offsetY: -12,
							font: 'bold 14px Arial',
							fill: new Fill({
								color: `#${this.primaryColor}`,
							}),
						}),
					}),
				];
			},
		});
	}

	initMap() {
		const zoomIn = document.createElement('span');
		const zoomOut = document.createElement('span');
		const maximize = document.createElement('span');
		const minimize = document.createElement('span');
		zoomIn.innerHTML =
			'<img src="/typo3conf/ext/atl_locations/Resources/Public/Icons/source_icons_plus.svg" width="32" height="32">';
		zoomOut.innerHTML =
			'<img src="/typo3conf/ext/atl_locations/Resources/Public/Icons/source_icons_minus.svg" width="32" height="32">';
		maximize.innerHTML =
			'<img src="/typo3conf/ext/atl_locations/Resources/Public/Icons/source_icons_maximize.svg" width="32" height="32">';
		minimize.innerHTML =
			'<img src="/typo3conf/ext/atl_locations/Resources/Public/Icons/source_icons_cancel.svg" width="32" height="32">';

		this.view = new View({
			padding: [50, 50, 50, 50],
			maxZoom: 15,
		});

		this.map = new OlMap({
			logo: false,
			target: this.mapDomElement,
			layers: [this.baseLayer, this.clusters],
			controls: defaultControls({
				attributionOptions: {
					collapsible: false,
				},
				zoomOptions: {
					zoomInLabel: zoomIn,
					zoomOutLabel: zoomOut,
				},
			}).extend([
				new FullScreen({
					label: maximize,
					labelActive: minimize,
				}),
			]),
			overlays: [this.popupOverlay],
			view: this.view,
		});
		if (this.features.length) {
			this.map.getView().fit(this.sourceVector.getExtent(), this.map.getSize());
		}
	}

	initEvents() {
		this.popupOverlayCloser.onclick = () => {
			this.popupOverlay.setPosition(undefined);
			this.popupOverlayCloser.blur();
			return false;
		};

		this.map.on('click', (evt) => {
			this.clusters.getFeatures(evt.pixel).then((features) => {
				if (features.length > 0) {
					const clusterMembers = features[0].get('features');
					if (clusterMembers.length > 1 && this.map.getView().getZoom() < 15) {
						// Calculate the extent of the cluster members.
						const extent = createEmpty();
						for (const feature of clusterMembers) {
							extend(extent, feature.getGeometry().getExtent());
						}
						const view = this.map.getView();
						// Zoom to the extent of the cluster members.
						view.fit(extent, { duration: 500, padding: [50, 50, 50, 50] });
					} else if (clusterMembers.length > 0) {
						const currentFeatureCollection = this.map.forEachFeatureAtPixel(
							evt.pixel,
							(data) => data.get('features'),
						);

						if (typeof currentFeatureCollection !== 'undefined') {
							let popupContent = '';

							$(currentFeatureCollection).each((index, feature) => {
								const dataCollection = feature.get('data');
								$.each(dataCollection, (subIndex, data) => {
									popupContent += '<div class=location-popup-item>';
									if (data.icon) {
										popupContent += `<img src="${data.icon}" alt="" /><br/>`;
									}
									popupContent += `<strong>${data.name}</strong>`;
									popupContent += '<br>';
									popupContent += `${data.address}<br>`;
									popupContent += `${data.zip}<br>`;
									if (data.phone) {
										popupContent += `Tel.: <a href="tel:${data.phone}">${data.phone}</a><br>`;
									}
									if (data.mail) {
										popupContent += `<a href="mailto:${data.mail}">E-Mail schreiben</a><br>`;
									}
									if (data.link) {
										popupContent += `<a href="${data.link}">Website</a><br>`;
									}
									popupContent += '</div>';
								});
							});
							this.popupOverlayContent.innerHTML = popupContent;
							const coordinate = evt.coordinate;
							this.popupOverlay.setPosition(coordinate);
						} else {
							this.popupOverlay.setPosition(undefined);
							this.popupOverlayCloser.blur();
						}
					}
				}
			});
		});

		// change mouse cursor when over marker
		this.map.on('pointermove', (e) => {
			const pixel = this.map.getEventPixel(e.originalEvent);
			const hit = this.map.hasFeatureAtPixel(pixel);
			this.map.getTarget().style.cursor = hit ? 'pointer' : '';
		});
	}

	updateMapFeatures = (event, isFilterActive, self) => {
		self.filterActive = isFilterActive;
		self.filteredLocationCollection = isFilterActive ? event.detail : [];
		self.map.removeLayer(self.clusters);
		self.features = [];
		self.initMarkerClustering();
		self.map.addLayer(self.clusters);
		self.initEvents();
		if (this.features.length) {
			self.map.getView().fit(this.sourceVector.getExtent(), self.map.getSize());
		}
	};
}
